/**
 * Renommage d'une valeur de référentiel Qualité (QUAL-001 §5.3).
 * Cloné de `LeadTypeEditDialog` — le `code`/slug reste stable (PATCH label).
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toastApiError } from '@/lib/toast-error'
import { useUpdateQualityReferential } from '@/hooks/useQualityReferentials'
import type { QualityReferentialItem } from '@/hooks/useQuality.types'

interface Props {
    target: Pick<QualityReferentialItem, 'id' | 'label'> | null
    entityId: string | null
    onClose: () => void
}

export function QualityReferentialEditDialog({ target, entityId, onClose }: Props) {
    const { t } = useTranslation()
    const [label, setLabel] = useState('')
    const updateMutation = useUpdateQualityReferential(entityId)

    useEffect(() => {
        if (target) setLabel(target.label)
    }, [target])

    if (!target) return null

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!label.trim()) return
        try {
            await updateMutation.mutateAsync({ id: target.id, patch: { label: label.trim() } })
            toast.success(t('quality.referentials.updateSuccess', 'Valeur mise à jour'))
            onClose()
        } catch (err) {
            toastApiError(err, t('quality.referentials.updateError', 'Impossible de mettre à jour la valeur'))
        }
    }

    return (
        <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <DialogHeader>
                        <DialogTitle>{t('quality.referentials.editTitle', 'Renommer la valeur')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2 py-4">
                        <Label htmlFor="qual-ref-label">{t('quality.referentials.label', 'Libellé')}</Label>
                        <Input
                            id="qual-ref-label"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            maxLength={120}
                            autoFocus
                        />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>
                            {t('common.cancel', 'Annuler')}
                        </Button>
                        <Button type="submit" disabled={!label.trim() || updateMutation.isPending}>
                            {t('common.save', 'Enregistrer')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
